<script lang="ts">
  import { snapScrollPosition } from '$lib/actions/snap-scroll-position';
  import { t } from '$lib/i18n';
  import { reducedMotion } from '$lib/stores/reduced-motion';
  import { cn } from '$lib/utils';
  import { onMount } from 'svelte';
  import QuickAddCard from './quick-add-card.svelte';
  import SliderButton from './ui/slider-button.svelte';
  import { drawer } from '$lib/stores/drawer';
  import QuickAddCardLoader from './quick-add-card-loader.svelte';
  import { getUUID } from '$lib/utils/uuid';
  import { AddToCartLocations } from '$lib/analytics';

  export let styles: {
    username: string;
    image: string;
    products: unknown[];
    date: string;
    caption: string;
    hideDate?: boolean;
  }[];

  $: yotpoProduct = (product: { product_url?: string }) => {
    return product?.product_url;
  };
  $: storyblokProduct = (product: { slug?: string }) => {
    return product?.slug;
  };

  // Carousel
  let carousel: HTMLElement;
  let index = 0;

  const id = 'styled-the-look' + getUUID();

  const handleCarousel = (
    to: 'left' | 'right' | number,
    behavior: 'instant' | 'smooth' = $reducedMotion ? 'instant' : 'smooth'
  ) => {
    if (to === 'left' && index !== null) {
      carousel.scrollTo({
        left: carousel.clientWidth * (index - 1),
        behavior
      });
    } else if (to === 'right' && index !== null) {
      carousel.scrollTo({
        left: carousel.clientWidth * (index + 1),
        behavior
      });
    } else if (typeof to === 'number') {
      carousel.scrollTo({
        left: carousel.clientWidth * to,
        behavior
      });
    }
  };

  onMount(() => {
    const initialIndex = $drawer.meta?.initialIndex;
    if (typeof initialIndex === 'number') {
      handleCarousel(initialIndex, 'instant');
    }
  });
</script>

<div class="relative h-full" {id}>
  <div
    class="flex h-full w-full snap-x snap-mandatory overflow-x-auto hide-scrollbar motion-reduce:transition-none"
    bind:this={carousel}
    use:snapScrollPosition={(i) => (index = i)}
  >
    {#if index !== null}
      {@const navigatorLocale = navigator.language || navigator.languages[0]}
      {#each styles as style}
        {@const date = new Date(style.date || '')}
        <!-- Main content -->
        <div
          class={cn(
            'h-full min-w-full overflow-y-auto snap-center snap-always',
            'transform-gpu transition-transform duration-500 motion-reduce:transition-none'
          )}
        >
          <div
            class="mx-2 mt-2 rounded-sm border border-primary/4 bg-primary/2 p-3 lg:mx-4 lg:mt-4"
          >
            <div class="flex justify-between">
              <p class="font-semibold text-sm">{style.username}</p>
              {#if !style.hideDate}
                <p class="text-sm text-primary/80">
                  {date.toLocaleDateString(navigatorLocale, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              {/if}
            </div>
            <div>
              <img
                src={style.image}
                alt={style.caption}
                class="my-4 aspect-square w-full rounded-md object-cover snap-center snap-always"
              />
            </div>
            <p class="text-md lg:text-sm">{style.caption}</p>
          </div>
          {#if style.products?.length > 0}
            <div class="mx-2 lg:mx-4">
              <div class="mt-2 flex flex-col gap-2 pb-20">
                {#each style.products as product}
                  {#if product}
                    {@const yotpoProductUrl = yotpoProduct(product)}
                    {@const storyblokProductHandle = storyblokProduct(product)}
                    {@const splitLink = yotpoProductUrl?.split('/')}
                    {@const handle = splitLink?.length
                      ? splitLink[splitLink.length - 1]
                      : storyblokProductHandle}
                    {#if handle}
                      <QuickAddCardLoader
                        {handle}
                        enabled={false}
                        let:loading
                        let:error
                        let:product
                        let:load
                      >
                        <QuickAddCard
                          class="min-w-[90%] flex-1 transform-gpu snap-center snap-always rounded-sm backdrop-blur"
                          on:intersect={load}
                          {loading}
                          {error}
                          {product}
                          emptyStateImage={style.image}
                          productHandle={handle}
                          addDrawerTargetId={id}
                          location={AddToCartLocations.STYLED_BY_YOU}
                        />
                      </QuickAddCardLoader>
                    {/if}
                  {/if}
                {/each}
              </div>
            </div>
          {/if}
        </div>
      {/each}
    {/if}
  </div>

  <!-- Desktop slider controls and indicator -->
  <div class="absolute bottom-0 w-full">
    <div class="mt-5 flex items-center justify-between bg-highlight p-4">
      <SliderButton
        variant="ghost"
        icon="chevron-left-bold"
        disabled={index === 0}
        on:click={() => handleCarousel('left')}
      />

      <div class="text-sm lining-nums tabular-nums text-primary/80">
        <span>{index + 1}</span>
        <span>{$t('shop-the-look.of')}</span>
        <span>{styles.length}</span>
      </div>

      <SliderButton
        variant="ghost"
        icon="chevron-right-bold"
        disabled={index === styles.length - 1}
        on:click={() => handleCarousel('right')}
      />
    </div>
  </div>
</div>
